/**
 * 非同期ページ遷移（swup）と、下から上へ辺が抜ける画面の切り替え
 *
 * @description 入れ替えるのは `.wrapper` ごと。ヘッダーもフッターも一緒に差し替わる。
 *              共通のまま外に置くと、ナビだけが辺と関係なく先に切り替わって浮いて見える。
 *
 *              切り替えは3本のトゥイーンで組む。
 *                1 出ていく側は上へ押し出されて薄くなる（灰色にはしない）
 *                2 入ってくる側は下から上がってくる
 *                3 現れる境目は直線。2倍に取ったマスクの位置だけを -100% → 0% と動かす
 *
 *              マスクは `.wrapper`、上下移動は `.wrapper__inner` が持つ。同じ要素に両方掛けると、
 *              ずらした分だけ「現れる境目」まで一緒に動いてしまう（マスクの % は要素の箱が基準）。
 *
 *              遷移中は新旧2枚の `.wrapper` が position:fixed で重なるため document の高さが消える。
 *              スクロールバーごと消えるとページ幅が変わってしまうので、高さだけ body に預けたうえで、
 *              見えている位置は `.wrapper__inner` の top で凍結する。
 */

import Swup from 'swup';
import SwupParallelPlugin from '@swup/parallel-plugin';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupBodyClassPlugin from '@swup/body-class-plugin';
import SwupPreloadPlugin from '@swup/preload-plugin';
import SwupA11yPlugin from '@swup/a11y-plugin';
import { gsap } from 'gsap/dist/gsap.min.js';
import { CustomEase } from 'gsap/dist/CustomEase.min.js';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger.min.js';
import { initMarquee, destroyMarquee } from './cmn-marquee';
import { initFooterParallax, destroyFooterParallax } from './cmn-footer';
import { stopSmoothScroll, startSmoothScroll, settleSmoothScroll } from './lib-smooth-scroll';
import { initHeaderReveal, destroyHeaderReveal } from './cmn-header';
import { initShareCopy } from './cmn-share';
import { initBackLink } from './cmn-backlink';
import { initBtnArrow } from './cmn-btn-arrow';
import { initMvSlider, destroyMvSlider } from './page-home_mv';
import { initInstaSlider, destroyInstaSlider } from './page-home_insta';
import { initCreatorsParallax, destroyCreatorsParallax, initCreatorsBg, destroyCreatorsBg } from './page-home_creators';
import { initCreatorsView, destroyCreatorsView } from './page-creators';
import { initTopicParallax, destroyTopicParallax } from './page-topics';
import { initServiceBloom, destroyServiceBloom } from './page-about_service';
import { initServiceFig, destroyServiceFig } from './page-about_fig';
import { initReveal, destroyReveal } from './cmn-reveal';
import { closeDrawer } from './cmn-drawer';
import { initListPager, destroyListPager } from './cmn-listpager';
import { initReleasePager, destroyReleasePager } from './page-creators_release';
import { stopFocusOutline } from './cmn-a11y';

gsap.registerPlugin(CustomEase, ScrollTrigger);

/** 入れ替える器 */
const CONTAINER = '.wrapper';

/** 器の中の移動レイヤー */
const INNER = '.wrapper__inner';

/** 切り替えの尺(秒) */
const DUR = 1.1;

/** 上下の押し出し(vh)。0にすると辺が抜けるだけになって奥行きが消える */
const SHIFT_VH = 20;

/** 出ていく側をどこまで薄くするか */
const OUT_FADE = 0.45;

/** 出ていく側のぼかし(px) */
const OUT_BLUR = 12;

/** 頭がゆっくり、真ん中で一気に、最後がまた丁寧。「スッと1回で」の形はここが作っている */
const EASE = CustomEase.create('wipeEase', '0.65, 0.01, 0.05, 0.99');

/**
 * 非同期遷移から外すパスのセグメント
 *
 * @description Contact Form 7 のスクリプトはフォームがあるページにしか読み込まれないので、
 *              非同期で開くと初期化が走らずバリデーションも確認ステップも死ぬ。
 *              フォーム系はまるごとフルロードに任せる
 */
const EXCLUDE_SEGMENTS = ['contact', 'audition', 'form', 'thanks'];

/** ページごとのスクロール位置。戻る/進むで復元する。key = 遷移先URL */
const scrollPositions = new Map<string, number>();

/** 預かっているあいだ保つスクロール位置 */
let holdY = 0;

/** 遷移中に預かったスクロール量。着地位置に足して返す */
let carryY = 0;

/** 直前の指の位置。touchmove の移動量を取るのに使う */
let touchY = 0;

/** 入ってくる側の移動レイヤー。遷移中はここを動かして、送ったぶんをその場で見せる */
let incomingInner: HTMLElement | null = null;

/** 入ってくる側で送れる下限。遷移先の高さで頭打ちにする */
let incomingMax = Number.POSITIVE_INFINITY;

/** 預かるのをやめる後始末。預かっていなければ null */
let unhold: (() => void) | null = null;

/** 遷移後に置きたいスクロール位置（通常は先頭0／戻る・進むは保存値） */
let enterY = 0;

/** モーションを抑える設定か */
const prefersReduced = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * URLが非同期遷移の対象外か判定する
 *
 * @param url 判定するURL（相対・絶対どちらでも可）
 * @returns 対象外なら true
 */
const isExcluded = (url: string): boolean => {
    let pathname = '';

    try {
        pathname = new URL(url, location.href).pathname;
    } catch {
        return false;
    }

    return pathname
        .split('/')
        .filter(Boolean)
        .some((segment) => EXCLUDE_SEGMENTS.includes(segment));
};

/**
 * いま表に出ている（新しい）器を返す
 *
 * @returns 器。見つからなければ null
 */
const activeContainer = (): HTMLElement | null =>
    document.querySelector<HTMLElement>(`${CONTAINER}:not(.is-previous-container)`)
    ?? document.querySelector<HTMLElement>(CONTAINER);

/**
 * 器の中の移動レイヤーを返す
 *
 * @param container 器
 * @returns 移動レイヤー。見つからなければ null
 */
const innerOf = (container: Element | null): HTMLElement | null =>
    container?.querySelector<HTMLElement>(INNER) ?? null;

/**
 * ページごとに作り直すモジュールを破棄する
 */
const destroyAll = (): void => {
    const offs = [closeDrawer, stopFocusOutline, destroyMarquee, destroyMvSlider, destroyInstaSlider, destroyCreatorsParallax, destroyCreatorsBg, destroyHeaderReveal, destroyFooterParallax, destroyCreatorsView, destroyTopicParallax, destroyServiceBloom, destroyServiceFig, destroyReveal, destroyListPager, destroyReleasePager];

    offs.forEach((off) => {
        try {
            off();
        } catch {
            // 1つの後片付けの失敗で他を止めない
        }
    });
};

/**
 * ページごとに作り直すモジュールを初期化する
 *
 * @param scope 新しい器。遷移中は古い器も残っているので探索範囲を絞る
 */
const initAll = (scope: ParentNode): void => {
    void initMarquee(scope);
    initMvSlider();
    initInstaSlider();
    initCreatorsParallax();
    initCreatorsBg();
    initCreatorsView();
    initHeaderReveal(scope);
    initFooterParallax();
    initTopicParallax();
    initServiceBloom();
    initServiceFig();
    initReveal();
    initListPager();
    initReleasePager();
    initShareCopy();
    initBackLink(scope);
    initBtnArrow(scope);
};

/**
 * 遷移中に使ったインラインの指定を全部落として、通常のレイアウトへ戻す
 */
const thaw = (): void => {
    document.body.style.removeProperty('min-height');

    document.querySelectorAll<HTMLElement>(CONTAINER).forEach((container) => {
        container.style.removeProperty('opacity');
        container.style.removeProperty('filter');
        container.style.removeProperty('mask-position');
        container.style.removeProperty('-webkit-mask-position');

        // 移動レイヤーのインラインは top も含めて全部この遷移で付けたもの。
        // GSAPが transform と併せて置く rotate/scale まで落とすので属性ごと消す
        innerOf(container)?.removeAttribute('style');
    });
};

/**
 * 預かるのをやめる
 */
const releaseHold = (): void => {
    unhold?.();
    unhold = null;
};

/**
 * 預かったぶんを、入ってくる側の見た目へ反映する
 *
 * @description 器がfixedのあいだ、入ってくる側は `top` を負にずらして見せている。
 *              そこを動かせば、めくれて出てくる絵が送った位置のまま現れる
 */
/**
 * 移動レイヤーを上へずらす
 *
 * @param inner 移動レイヤー
 * @param y 画面座標で何pxぶん上へ送るか
 * @returns {void}
 */
const shiftLayer = (inner: HTMLElement, y: number): void => {
    inner.style.top = `${-y}px`;
    // 遷移のあいだ、このレイヤーは transform を持つので中の position:fixed の基準が
    // 画面からレイヤーへ移る。画面に貼り付いていたい面が同じだけずれるので、
    // 戻すのに要る量をCSSからも読めるようにしておく
    inner.style.setProperty('--layer-shift', `${y}px`);
};

const paintCarry = (): void => {
    carryY = Math.min(Math.max(carryY, -enterY), incomingMax - enterY);

    if (incomingInner) shiftLayer(incomingInner, enterY + carryY);
};

/**
 * 遷移中のスクロール操作を預かりはじめる
 *
 * @description 器がfixedのあいだは何をしても絵が動かないので、操作をそのまま通すと
 *              「効かなかったはずの量」が遷移後にまとめて効いてページが流れる。
 *              動かさずに量だけ数えておき、着地位置へ足して返す
 * @param y 預かっているあいだ保つスクロール位置
 */
const holdScroll = (y: number): void => {
    releaseHold();
    holdY = y;
    carryY = 0;
    incomingInner = null;
    incomingMax = Number.POSITIVE_INFINITY;

    /**
     * ホイールは動かさず量だけ数える。Lenisのホイール倍率は既定の1なのでそのまま足せる
     *
     * @description 捕捉フェーズで止めるのは、Lenisのリスナーがwindowのバブル側に付いていて、
     *              素通しすると stop() 中でも目標値だけ書き換わり、再開した瞬間にそこへ滑るため
     */
    const onWheel = (e: WheelEvent): void => {
        carryY += e.deltaY;
        paintCarry();

        e.stopPropagation();

        if (e.cancelable) e.preventDefault();
    };

    const onTouchStart = (e: TouchEvent): void => {
        touchY = e.touches[0]?.clientY ?? 0;
    };

    /** 指は下へ払うと上へ戻るので、移動量の符号を反転して足す */
    const onTouchMove = (e: TouchEvent): void => {
        const now = e.touches[0]?.clientY ?? touchY;

        carryY += touchY - now;
        touchY = now;
        paintCarry();

        e.stopPropagation();

        if (e.cancelable) e.preventDefault();
    };

    // 位置そのものは動かさない。遷移中はブラウザ側も（戻る/進むの復元などで）位置を書くので、
    // 数えるのはホイールと指の操作だけにする。ここで差分を足すとその書き込みまで持ち越してしまう
    const pin = (): void => {
        if (Math.round(window.scrollY) !== holdY) window.scrollTo(0, holdY);
    };

    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    window.addEventListener('scroll', pin);

    unhold = () => {
        window.removeEventListener('wheel', onWheel, true);
        window.removeEventListener('touchstart', onTouchStart, true);
        window.removeEventListener('touchmove', onTouchMove, true);
        window.removeEventListener('scroll', pin);
    };
};

/**
 * 預かったぶんを着地位置へ足す
 *
 * @description 新しい中身に入れ替わったあとに呼ぶ。行き止まりの先へは進めないので、
 *              遷移先の高さで頭打ちにする
 */
const applyCarry = (): void => {
    incomingInner = null;

    if (!carryY) return;

    const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const to = Math.min(Math.max(window.scrollY + carryY, 0), max);

    carryY = 0;
    window.scrollTo({ top: to, left: 0, behavior: 'instant' as ScrollBehavior });
};

/**
 * 遷移直前に、いま見えている位置を凍結する
 *
 * @description `.wrapper` が fixed になる直前に呼ぶ。document の高さを body へ預けてから
 *              移動レイヤーを現在のスクロール量ぶん上へずらすので、見た目は動かない
 */
const freeze = (): void => {
    // 器がfixedになるあいだは慣性で位置を動かされたくない
    stopSmoothScroll();

    const y = window.scrollY;

    document.body.style.minHeight = `${document.documentElement.scrollHeight}px`;

    const inner = innerOf(activeContainer());

    if (inner) shiftLayer(inner, y);

    // 絵が動かないあいだの操作は、量だけ預かって着地位置へ返す
    holdScroll(y);
};

/**
 * 入ってくる側を、辺が抜ける前の状態（隠れて下に沈んでいる）に整える
 */
const armIncoming = (): void => {
    const containers = [...document.querySelectorAll<HTMLElement>(CONTAINER)];
    const incoming = containers.find((el) => !el.classList.contains('is-previous-container'));

    if (!incoming) return;

    const pos = '0% -100%';

    incoming.style.maskPosition = pos;
    incoming.style.setProperty('-webkit-mask-position', pos);

    const inner = innerOf(incoming);

    if (inner) {
        gsap.set(inner, { y: `${SHIFT_VH}vh` });

        // ここから先は預かったぶんもこのレイヤーで見せる。遷移前に送られていた量も追いつかせる
        incomingInner = inner;
        incomingMax = Math.max(0, inner.scrollHeight - window.innerHeight);
        paintCarry();
    }
};

/**
 * 画面の切り替えを再生する
 *
 * @returns アニメーションの完了を待つPromise
 */
const playWipe = (): Promise<void> => {
    const containers = [...document.querySelectorAll<HTMLElement>(CONTAINER)];
    const outgoing = containers.find((el) => el.classList.contains('is-previous-container')) ?? null;
    const incoming = containers.find((el) => !el.classList.contains('is-previous-container')) ?? null;

    // モーションを抑える設定では動かさない。伏せた状態のまま返すと隠れたまま出るので、
    // armIncoming が置いた「隠れて沈んでいる」状態をここで解いておく
    if (!incoming || !outgoing || prefersReduced()) {
        incoming?.style.removeProperty('mask-position');
        incoming?.style.removeProperty('-webkit-mask-position');
        innerOf(incoming)?.removeAttribute('style');

        return Promise.resolve();
    }

    const outInner = innerOf(outgoing);
    const inInner = innerOf(incoming);
    const edge = { value: 100 };

    /** 現れる境目を引き直す。100で下に外れて隠れ、0で全部出る */
    const put = (): void => {
        const pos = `0% ${(-edge.value).toFixed(2)}%`;

        incoming.style.maskPosition = pos;
        incoming.style.setProperty('-webkit-mask-position', pos);
    };

    put();

    return new Promise<void>((resolve) => {
        const tl = gsap.timeline({
            defaults: { duration: DUR, ease: EASE },
            onComplete: () => resolve(),
        });

        // 出ていく側。上へ押し出されて薄くなる
        tl.to(outgoing, { opacity: OUT_FADE, filter: `blur(${OUT_BLUR}px)` }, 0);

        if (outInner) tl.to(outInner, { y: `-${SHIFT_VH}vh` }, 0);

        // 入ってくる側。下から上がってくる
        if (inInner) tl.to(inInner, { y: '0vh' }, 0);

        // 現れる境目。画面いっぱいを通る
        tl.to(edge, { value: 0, onUpdate: put }, 0);
    });
};

/**
 * 遷移後のスクロール位置を確定する
 *
 * @description 滑らせると、着地までのあいだ絶対配置の飾りが引きずられて見えるので即時に置く。
 *              慣性スクロール側の位置合わせは resumeScroll が受け持つ
 */
const settleScroll = (hash: string): void => {
    // セレクタとして無効な文字が入っていても落ちないよう id で引く
    const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;

    if (target) {
        // scroll-margin-top を効かせたいので scrollTo ではなく scrollIntoView で寄せる
        target.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
        return;
    }

    window.scrollTo({ top: enterY, left: 0, behavior: 'instant' as ScrollBehavior });
};

/**
 * 慣性スクロールを新しいページの高さと位置に合わせ直して再開する
 */
const resumeScroll = (): void => {
    settleSmoothScroll();
    startSmoothScroll();
};

/**
 * swupを起動して、切り替えを配線する
 */
const initSwup = (): void => {
    if (!document.querySelector(CONTAINER)) return;

    // フォーム系のページでは起動しない（全遷移フルロード）
    if (isExcluded(location.pathname)) return;

    history.scrollRestoration = 'manual';

    const swup = new Swup({
        containers: [CONTAINER],
        animateHistoryBrowsing: true,
        ignoreVisit: (url: string, { el }: { el?: Element } = {}) => {
            // 同一ページ内のアンカーはCSSのスムーススクロールに任せる。
            // swupが拾うと同一ページ遷移として先頭へ戻してしまう
            const anchor = el?.closest<HTMLAnchorElement>('a[href*="#"]');

            if (anchor) {
                const to = new URL(anchor.href, location.href);

                if (to.hash && to.pathname === location.pathname) return true;
            }

            return !!el?.closest('[data-no-swup]') || isExcluded(url);
        },
        plugins: [
            new SwupBodyClassPlugin(), // ページ別CSSスコープ（.slug-* / .is-thanks）の生命線
            new SwupHeadPlugin(),
            // 既定では今いるページも読み直してキャッシュに入れる。トップでは本文115KBが
            // FVの写真と回線を取り合って表示が遅れるので止める（戻ってきたときに取り直すだけ）
            new SwupPreloadPlugin({ preloadInitialPage: false }),
            new SwupA11yPlugin(),
            new SwupParallelPlugin({ containers: [CONTAINER] }), // 新旧の器を同時に持たせる
        ],
    });

    // 離脱するページのスクロール位置を控える。戻る/進むのときだけ復元する
    swup.hooks.on('visit:start', (visit) => {
        if (visit.from.url) scrollPositions.set(visit.from.url, window.scrollY);

        enterY = visit.history.popstate ? scrollPositions.get(visit.to.url) ?? 0 : 0;
    });

    // `.is-changing` が付いて器がfixedになる直前。ここで見えている位置を凍結する
    swup.hooks.before('animation:out:start', freeze);

    // 新しい器が差し込まれた直後。辺が抜ける前の状態に整えてからモジュールを組み直す
    swup.hooks.on('content:replace', () => {
        armIncoming();
        destroyAll();

        const scope = activeContainer();

        if (scope) initAll(scope);
    });

    // 器がfixedのあいだのスクロールはswupに触らせない（遷移後に自分で確定する）
    swup.hooks.replace('content:scroll', () => true);

    // 新旧の器が揃った状態で切り替えを回す
    swup.hooks.replace('animation:in:await', (_visit, { skip }) => (skip ? undefined : playWipe()));

    // `.is-changing` が外れて通常のレイアウトへ戻ったあと。位置を確定して測り直させる
    swup.hooks.on('visit:end', (visit) => {
        thaw();
        settleScroll(visit.to.hash);
        applyCarry();
        releaseHold();
        resumeScroll();
        ScrollTrigger.refresh();
    });

    // 途中で止まった・失敗したときも凍結を解く
    swup.hooks.on('visit:abort', () => {
        thaw();
        releaseHold();
        resumeScroll();
    });
    swup.hooks.on('visit:fail', () => {
        thaw();
        releaseHold();
        resumeScroll();
    });
};

initSwup();
